import { config } from 'config';
import { documentTypeOptions } from 'shared/fixtures';
import isEmpty from 'lodash/isEmpty';
import { theme } from 'styled';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Avatar,
    InputHidden,
    ProfileContainer,
    ProfileIcon,
    ProfileText,
    ResponsiveCol,
    StyledRow
} from './styled';
import {
    ButtonFooter,
    Checkbox,
    ExpandButton,
    FormGroup,
    Input,
    Loader,
    MaterialIcon,
    SelectSearch,
    TagList
} from 'mol-ui';
import {
    Col,
    Row
} from 'reactstrap';
import {
    CompanyPicker,
    CoveragePicker,
    DoctorPicker,
    WorkplacePicker
} from 'views/pickers';
import {
    ConfirmationModal,
    FormLayout,
    PasswordSecurityCheck
} from 'views/components';
import React, { useEffect, useRef, useState } from 'react';
import {
    checkPasswordSecurity,
    clearObject,
    parseSelect,
    validationSchema
} from 'shared/utils';
import {
    createUser,
    deleteUser,
    getUser,
    setUserRegistrationError,
    updateUser
} from 'core/User';
import {
    useDispatch,
    useErrors,
    useObjectState,
    useSelector
} from 'hooks';

const {
    company,
    coverages,
    identificationFreeLength,
    patientIdentificationAlpha,
    restrictedMenu,
    ssoLogin,
    workplaces
} = config;

const UserRegistration = () => {
    
    const { pathname } = useLocation();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const {
        user: profile
    } = useSelector('account');
    const { configurations } = useSelector('config');
    const strongPasswordRequired = configurations?.strong_password_required === '1';
    const {
        registrationError,
        loading,
        selected: user
    } = useSelector('user');

    const availableActions = [{
        key: 'call_history',
        label: t('Ver historial de llamadas')
    }, {
        key: 'call_data',
        label: t('Acceder a información de las llamadas')
    }, {
        key: 'patient_info',
        label: t('Ver información del paciente')
    }, {
        key: 'appointments',
        label: t('Ver y editar turnos')
    }, {
        key: 'patients_files',
        label: t('Ver y editar archivos de pacientes')
    }, {
        key: 'professionals_files',
        label: t('Ver y editar archivos de otros usuarios')
    }, {
        key: 'patients_create',
        label: t('Crear pacientes')
    }, {
        key: 'patients_edit',
        label: t('Editar pacientes')
    }, {
        key: 'professionals_create',
        label: t('Crear profesionales')
    }, {
        key: 'professionals_edit',
        label: t('Editar profesionales')
    }, {
        key: 'show_professionals_listing',
        label: t('Ver el listado de profesionales en el inicio')
    }, {
        display: !restrictedMenu.includes('statistics'),
        key: 'view_statistics',
        label: t('Ver estadísticas')
    }, {
        key: 'download_reports',
        label: t('Descargar reportes')
    }].filter(({ display = true }) => !!display);

    const { clearError, errors, setErrors } = useErrors();
    const [file, setFile] = useState(null);
    const [form, , setFormValue, setFormValues] = useObjectState({
        avatar: '',
        cellphone_number: '',
        companies_related: [],
        confirmPassword: null,
        country_code: '',
        coverages_related: [],
        doctors_related: [],
        email: '',
        first_name: '',
        identification: '',
        identification_id: 1,
        last_name: '',
        locked_actions: [],
        position: '',
        restricted_access: 0,
        show_companies: 0,
        show_coverages: 0,
        show_doctors: 0,
        show_patients: 0,
        show_workplaces: 0,
        workplaces_related: []
    });
    const [formLoaded, setFormLoaded] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [selectedCoverage, setSelectedCoverage] = useState(null);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [selectedWorkplace, setSelectedWorkplace] = useState(null);
    const avatarRef = useRef(null);

    const editMode = pathname.indexOf(t('/usuarios/editar')) >= 0;
    const registrationMode = pathname.indexOf(t('/usuarios/crear')) >= 0;
    const viewMode = pathname.indexOf(t('/usuarios/ver')) >= 0;

    useEffect(() => {
        if (editMode || viewMode) {
            dispatch(getUser(pathname.split('/')[3]));
        }
    }, [editMode, pathname, viewMode]);

    useEffect(() => {
        dispatch(setUserRegistrationError({}));
    }, []);

    useEffect(() => {
        if ((editMode || viewMode) && !formLoaded && !isEmpty(user)) {
            setFormValues(clearObject({ ...user, avatar: user?.avatar?.url }));
            setFormLoaded(true);
        }
    }, [editMode, formLoaded, pathname, setFormLoaded, setFormValues, user, viewMode]);

    useEffect(() => {
        if (!formLoaded && pathname === t('/perfil')) {
            setFormValues(clearObject({ ...profile, avatar: profile?.avatar?.url }));
            setFormLoaded(true);
        }
    }, [formLoaded, pathname, profile, setFormLoaded, setFormValues, user]);

    const handleChange = input => value => {
        clearError(input);
        if (['email', 'identification'].includes(input)) {
            const newCreationError = { ...registrationError };
            if (newCreationError[input]) {
                delete newCreationError[input];
                dispatch(setUserRegistrationError(newCreationError));
            }
        }
        setFormValue(input, value);
    };

    const handleCompanyAdd = () => {
        setFormValue('companies_related', [...form.companies_related, selectedCompany]);
        setSelectedCompany(null);
    };

    const handleCoverageAdd = () => {
        const { label, value } = selectedCoverage;
        setFormValue('coverages_related', [...form.coverages_related, { label, value }]);
        setSelectedCoverage(null);
    };

    const handleCompanyRemove = ({ value: removeId }) => {
        setFormValue('companies_related', form.companies_related.filter(({ value }) => value !== removeId));
    };

    const handleCoverageRemove = ({ value: removeId }) => {
        setFormValue('coverages_related', form.coverages_related.filter(({ value }) => value !== removeId));
    };

    const handleDoctorAdd = () => {
        setFormValue('doctors_related', [...form.doctors_related, selectedDoctor]);
        setSelectedDoctor(null);
    };

    const handleDoctorRemove = ({ value: removeId }) => {
        setFormValue('doctors_related', form.doctors_related.filter(relatedDoctor => relatedDoctor?.id !== removeId));
    };

    const handleWorkplaceAdd = () => {
        setFormValue('workplaces_related', [...form.workplaces_related, selectedWorkplace]);
        setSelectedWorkplace(null);
    };

    const handleWorkplaceRemove = ({ value: removeId }) => {
        setFormValue('workplaces_related', form.workplaces_related.filter(({ value }) => value !== removeId));
    };

    const handleImageSelect = () => {
        const element = avatarRef.current;
        const FR = new FileReader();
        FR.addEventListener('load', ({ target: { result } }) => {
            setFormValue('avatar', result);
            element.value = '';
        });
        FR.readAsDataURL(element.files[0]);
        setFile(element.files[0]);
    };

    const handleLockedAction = (id, newValue) => {
        setFormValue('locked_actions', !newValue ? [...form.locked_actions, id] : form.locked_actions.filter(action => action !== id));
    };

    const handleSubmit = () => {
        const registrationMode = pathname === t('/usuarios/crear');
        const answerForm = { ...form };
        if (!validate()) return;
        answerForm.companies_related = answerForm.companies_related.map(({ value }) => value);
        answerForm.coverages_related = answerForm.coverages_related.map(({ value }) => value);
        answerForm.doctors_related = answerForm.doctors_related.map(relatedDoctor => relatedDoctor?.id);
        answerForm.workplaces_related = answerForm.workplaces_related.map(({ value }) => value);
        const commonParams = {
            ...(!!file && { file })
        };
        if (registrationMode) {
            const payload = {
                ...commonParams,
                form: { ...answerForm, ...(!!profile.restricted_access && { restricted_access: 1 }) }
            };
            dispatch(createUser(payload));
        } else {
            const payload = {
                ...commonParams,
                form: clearObject({
                    ...answerForm,
                    ...(ssoLogin && { identification: undefined, identification_id: undefined, identification_type: undefined })
                }),
                pathname
            };
            dispatch(updateUser(clearObject(payload)));
        }
    };

    const isLockedActionDisabled = id => id === 'professionals_files' && form.locked_actions.includes('patients_files');

    const modalResponse = value => {
        if (value === 'confirm') {
            const { id } = user ?? {};
            dispatch(deleteUser(id));
        }
        setModalOpen(false);
    };

    const validate = () => {
        const { errors, isValid } = validationSchema({
            cellphone_number: [!!form.cellphone_number, t('Requerido')],
            email: [!!form.email, t('Requerido')],
            first_name: [!!form.first_name, t('Requerido')],
            identification: [!!form.identification, t('Requerido')],
            last_name: [!!form.last_name, t('Requerido')],
            password: [
                [!registrationMode || !!form.password, t('Requerido')],
                [!form.password || (form.password === form.confirmPassword), t('Las contraseñas deben coincidir')],
                [!strongPasswordRequired || !form.password || checkPasswordSecurity(form.password).isValid, t('La contraseña no es segura')]
            ],
            position: [!!form.position, t('Requerido')]
        });
        setErrors(errors);
        return isValid;
    };

    const title = pathname === t('/usuarios/crear')
        ? t('Nuevo usuario')
        : (pathname === t('/perfil') ?
            t('Editar perfil')
            : t('Editar usuario'));
    const goBackTo = pathname === t('/perfil') ? t('/dashboard') : t('/usuarios/listar');
    const isOwnProfile = pathname.split('/')[1] === t('perfil');
    const { restricted_access } = profile;

    if (loading) {
        return (
            <Loader active={ true } />
        );
    }

    return (
        <FormLayout goBackName={ `${ t('Volver al') } ${ goBackTo === t('/dashboard') ? t('dashboard') : ('listado') }` } goBackTo={ goBackTo } title={ title }>
            <Row>
                <ProfileContainer onClick={ !viewMode ? (() => avatarRef.current.click()) : () => null }>
                    <ProfileIcon>
                        {
                            form.avatar ?
                                <Avatar src={ form.avatar }  /> :
                                <MaterialIcon color={ theme.opacityColor } icon="face" size={ 70 } />
                        }
                    </ProfileIcon>
                    <ProfileText>{ t('Subir foto de perfil') }</ProfileText>
                </ProfileContainer>
            </Row>
            <Row>
                <Col md="6" sm="12">
                    <FormGroup label={ t('Nombre') }>
                        <Input
                            disabled={ viewMode }
                            error={ errors.first_name }
                            value={ form.first_name ?? '' }
                            onChange={ handleChange('first_name') }
                        />
                    </FormGroup>
                </Col>
                <Col md="6" sm="12">
                    <FormGroup label={ t('Apellido') }>
                        <Input
                            disabled={ viewMode }
                            error={ errors.last_name }
                            value={ form.last_name ?? '' }
                            onChange={ handleChange('last_name') }
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                {
                    !ssoLogin &&
                    <>
                        <Col sm="2" xs="12">
                            <FormGroup label={ t('Tipo') } marginBottom="20px">
                                <SelectSearch
                                    disabled={ viewMode }
                                    options={ documentTypeOptions }
                                    value={ form.identification_id ?? '' }
                                    width="100%"
                                    onChange={ handleChange('identification_id') }
                                />
                            </FormGroup>
                        </Col>
                        <Col>
                            <FormGroup label={ t('Documento') }>
                                <Input
                                    disabled={ viewMode }
                                    error={ errors.identification ?? registrationError.identification }
                                    maxLength={ identificationFreeLength ? undefined : 8 }
                                    type={ patientIdentificationAlpha ? 'text' : 'number' }
                                    value={ form.identification ?? '' }
                                    onChange={ handleChange('identification') }
                                />
                            </FormGroup>
                        </Col>
                    </>
                }
                <Col md="6" sm="6">
                    <FormGroup label={ t('N° Telefono') }>
                        <Input
                            disabled={ viewMode }
                            error={ errors.cellphone_number }
                            type="number"
                            value={ form.cellphone_number ?? '' }
                            onChange={ handleChange('cellphone_number') }
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col md="6">
                    <FormGroup label={ t('Cargo') }>
                        <Input
                            disabled={ viewMode }
                            error={ errors.position }
                            type="text"
                            value={ form.position ?? '' }
                            onChange={ handleChange('position') }
                        />
                    </FormGroup>
                </Col>
                <Col md="6">
                    <FormGroup label={ t('Email') }>
                        <Input
                            autocomplete="off"
                            disabled={ viewMode }
                            error={ errors.email ?? registrationError.email }
                            type="email"
                            value={ form.email ?? '' }
                            onChange={ handleChange('email') }
                        />
                    </FormGroup>
                </Col>
            </Row>
            {
                !restricted_access && !isOwnProfile &&
                <StyledRow>
                    <Col md="6">
                        <FormGroup marginBottom="25px">
                            <Checkbox
                                checked={ !!form.restricted_access }
                                disabled={ viewMode }
                                label={ t('Acceso restringido') }
                                onClick={ () => setFormValue('restricted_access', !!form.restricted_access ? 0 : 1) }
                            />
                        </FormGroup>
                    </Col>
                </StyledRow>
            }
            {
                !restricted_access && !!form.restricted_access &&
                <>
                    <Row>
                        <StyledRow>
                            <Col md="6">
                                <FormGroup marginBottom="10px">
                                    <Checkbox
                                        checked={ !!form.show_coverages }
                                        disabled={ viewMode }
                                        label={ t('Asociar todas las coberturas') }
                                        onClick={ () => setFormValue('show_coverages', !!form.show_coverages ? 0 : 1) }
                                    />
                                </FormGroup>
                            </Col>
                        </StyledRow>
                        {
                            coverages && !viewMode && !form.show_coverages &&
                            <StyledRow>
                                <Col md="6" xs="9">
                                    <FormGroup label={ t('Coberturas') }>
                                        <CoveragePicker
                                            objectChange
                                            filter={ ({ value }) => !form.coverages_related.find(({ value: id }) => value === id) }
                                            value={ selectedCoverage?.id }
                                            width="100%"
                                            onChange={ setSelectedCoverage }
                                        />
                                    </FormGroup>
                                </Col>
                                <ResponsiveCol>
                                    <ExpandButton
                                        circle
                                        disabled={ !selectedCoverage }
                                        fontSize={ 25 }
                                        icon="add"
                                        size={ 40 }
                                        onClick={ handleCoverageAdd }
                                    />
                                </ResponsiveCol>
                            </StyledRow>
                            
                        }
                        {
                            coverages && !isEmpty(form.coverages_related) && !form.show_all_coverage &&
                            <Col xs="12">
                                <FormGroup label={ viewMode ? t('Coberturas') : '' }>
                                    <TagList
                                        items={ form.coverages_related }
                                        margin="5px 0px 10px 0px"
                                        readOnly={ !!viewMode }
                                        onRemove={ handleCoverageRemove }
                                    />
                                </FormGroup>
                            </Col>
                        }
                    </Row>
                    <Row>
                        <StyledRow>
                            <Col md="6">
                                <FormGroup marginBottom="10px">
                                    <Checkbox
                                        checked={ !!form.show_doctors }
                                        disabled={ viewMode }
                                        label={ t('Asociar todos los profesionales') }
                                        onClick={ () => setFormValue('show_doctors', !!form.show_doctors ? 0 : 1) }
                                    />
                                </FormGroup>
                            </Col>
                        </StyledRow>
                        {
                            !viewMode && !form.show_doctors &&
                            <StyledRow>
                                <Col md="6" xs="9">
                                    <FormGroup label={ t('Profesionales asociados') }>
                                        <DoctorPicker
                                            objectChange
                                            filter={ ({ value }) => !form.doctors_related.find(relatedDoctor => value === relatedDoctor?.id) }
                                            value={ selectedDoctor?.id }
                                            onChange={ setSelectedDoctor }
                                        />
                                    </FormGroup>
                                </Col>
                                <ResponsiveCol>
                                    <ExpandButton
                                        circle
                                        disabled={ !selectedDoctor }
                                        fontSize={ 25 }
                                        icon="add"
                                        size={ 40 }
                                        onClick={ handleDoctorAdd }
                                    />
                                </ResponsiveCol>
                            </StyledRow>
                        }
                        {
                            !isEmpty(form.doctors_related) && !form.show_doctors &&
                            <Col xs="12">
                                <FormGroup label={ viewMode ? t('Profesionales asociados') : '' }>
                                    <TagList
                                        items={ parseSelect(form.doctors_related) }
                                        margin="0px 0px 20px 0px"
                                        readOnly={ !!viewMode }
                                        onRemove={ handleDoctorRemove }
                                    />
                                </FormGroup>
                            </Col>
                        }
                    </Row>
                    {
                        company &&
                        <>
                            <StyledRow>
                                <Col md="6">
                                    <FormGroup marginBottom="10px">
                                        <Checkbox
                                            checked={ !!form.show_companies }
                                            disabled={ viewMode }
                                            label={ t('Asociar todas las empresas') }
                                            onClick={ () => setFormValue('show_coverages', !!form.show_coverages ? 0 : 1) }
                                        />
                                    </FormGroup>
                                </Col>
                            </StyledRow>
                            <Row>
                                {
                                    !viewMode && !form.show_companies &&
                                    <StyledRow>
                                        <Col md="6" xs="9">
                                            <FormGroup label={ t('Empresas') }>
                                                <CompanyPicker
                                                    objectChange
                                                    filter={ ({ value }) => !form.companies_related.find(({ value: id }) => value === id) }
                                                    value={ selectedCompany?.id }
                                                    width="100%"
                                                    onChange={ setSelectedCompany }
                                                />
                                            </FormGroup>
                                        </Col>
                                        <ResponsiveCol>
                                            <ExpandButton
                                                circle
                                                disabled={ !selectedCompany }
                                                fontSize={ 25 }
                                                icon="add"
                                                size={ 40 }
                                                onClick={ handleCompanyAdd }
                                            />
                                        </ResponsiveCol>
                                    </StyledRow>
                                    
                                }
                                {
                                    !isEmpty(form.companies_related) && !form.show_companies &&
                                    <Col xs="12">
                                        <FormGroup label={ viewMode ? t('Empresas') : '' }>
                                            <TagList
                                                items={ form.companies_related }
                                                margin="5px 0px 10px 0px"
                                                readOnly={ !!viewMode }
                                                onRemove={ handleCompanyRemove }
                                            />
                                        </FormGroup>
                                    </Col>
                                }
                            </Row>
                        </>
                    }
                    {
                        workplaces &&
                        <>
                            <StyledRow>
                                <Col md="6">
                                    <FormGroup marginBottom="10px">
                                        <Checkbox
                                            checked={ !!form.show_workplaces }
                                            disabled={ viewMode }
                                            label={ t('Asociar todos los lugares de trabajo') }
                                            onClick={ () => setFormValue('show_workplaces', !!form.show_workplaces ? 0 : 1) }
                                        />
                                    </FormGroup>
                                </Col>
                            </StyledRow>
                            <Row>
                                {
                                    !viewMode && !form.show_workplaces &&
                                    <StyledRow>
                                        <Col md="6" xs="9">
                                            <FormGroup label={ t('Lugar de trabajo') }>
                                                <WorkplacePicker
                                                    objectChange
                                                    filter={ ({ value }) => !form.workplaces_related.find(({ value: id }) => value === id) }
                                                    value={ selectedWorkplace?.id }
                                                    width="100%"
                                                    onChange={ setSelectedWorkplace }
                                                />
                                            </FormGroup>
                                        </Col>
                                        <ResponsiveCol>
                                            <ExpandButton
                                                circle
                                                disabled={ !selectedWorkplace }
                                                fontSize={ 25 }
                                                icon="add"
                                                size={ 40 }
                                                onClick={ handleWorkplaceAdd }
                                            />
                                        </ResponsiveCol>
                                    </StyledRow>
                                    
                                }
                                {
                                    !isEmpty(form.workplaces_related) && !form.show_workplaces &&
                                    <Col xs="12">
                                        <FormGroup label={ viewMode ? t('Lugares de trabajo') : '' }>
                                            <TagList
                                                items={ form.workplaces_related }
                                                margin="5px 0px 10px 0px"
                                                readOnly={ !!viewMode }
                                                onRemove={ handleWorkplaceRemove }
                                            />
                                        </FormGroup>
                                    </Col>
                                }
                            </Row>
                        </>
                    }
                    <StyledRow>
                        <Col md="6">
                            <FormGroup marginBottom="10px">
                                <Checkbox
                                    checked={ !!form.show_patients }
                                    disabled={ viewMode }
                                    label={ t('Asociar todos los pacientes') }
                                    onClick={ () => setFormValue('show_patients', !!form.show_patients ? 0 : 1) }
                                />
                            </FormGroup>
                        </Col>
                    </StyledRow>
                    <StyledRow>
                        <Col xs="12">
                            <FormGroup label={ t('Permisos sobre la plataforma') } marginBottom="20px">
                                {
                                    availableActions.map(({
                                        key,
                                        label
                                    }) => {
                                        const isChecked = !form.locked_actions.includes(key);
                                        return (
                                            <Checkbox
                                                checked={ isChecked }
                                                disabled={ viewMode || isLockedActionDisabled(key) }
                                                key={ key }
                                                label={ label }
                                                margin="0px 0px 10px 0px"
                                                onClick={ () => handleLockedAction(key, !isChecked) }
                                            />
                                        );
                                    })
                                }
                            </FormGroup>
                        </Col>
                    </StyledRow>
                </>
            }
            {
                !viewMode &&
                <StyledRow>
                    <Col md="6" sm="12">
                        <FormGroup label={ t('Contraseña') }>
                            <Input
                                disabled={ viewMode }
                                error={ errors.password }
                                type="password"
                                value={ form.password ?? '' }
                                onChange={ handleChange('password') }
                            />
                            { strongPasswordRequired && <PasswordSecurityCheck password={ form.password } /> }
                        </FormGroup>
                    </Col>
                    <Col md="6" sm="12">
                        <FormGroup label={ t('Confirmar Contraseña') }>
                            <Input
                                disabled={ viewMode }
                                error={ errors.password }
                                type="password"
                                value={ form.confirmPassword ?? '' }
                                onChange={ handleChange('confirmPassword') }
                            />
                        </FormGroup>
                    </Col>
                </StyledRow>
            }
            <Row>
                <ButtonFooter>
                    {
                        !viewMode && !restricted_access && !registrationMode && !isOwnProfile &&
                        <ExpandButton
                            danger
                            caption={ t('Eliminar') }
                            size={ 100 }
                            onClick={ () => setModalOpen(true) }
                        />
                    }
                    {
                        !viewMode &&
                        <ExpandButton
                            caption={ t('Confirmar') }
                            disabled={ viewMode }
                            size={ 100 }
                            onClick={ handleSubmit }
                        />
                    }
                </ButtonFooter>
            </Row>
            <InputHidden
                accept="image/*"
                ref={ avatarRef }
                type="file"
                onChange={ handleImageSelect }
            />
            <ConfirmationModal
                actionHandler={ modalResponse }
                active={ modalOpen }
                title={ t('¿Quieres dar de baja este usuario?') }
            />
        </FormLayout>
    );
};

UserRegistration.propTypes = {

};

export default UserRegistration;
